import { isActionOf } from "typesafe-actions";

import Sentry from "@similarweb/sentry-logger";

import { ListVariant, type ListParams } from "app/pages/sales-intelligence/domain/lists/types/list";
import { selectActiveLeadsType, selectIsAggregatedDataUsed } from "app/pages/sales-intelligence/modules/leads-search";
import { createMoveTopNLeadsToListThunk } from "app/pages/sales-intelligence/modules/leads-search/store/effects";
import type { MoveTopNLeadsParams } from "app/pages/sales-intelligence/modules/leads-search/store/types";
import {
    getOpportunityListTableQueryKeys,
    opportunityContactsQueryKeys,
} from "app/pages/sales-intelligence/pages/opportunity-list/query/queryKeyFactory";
import type { FetchTopNLeadsDomainsFnParams } from "app/pages/sales-intelligence/pages/opportunity-list/query/types";
import { createContactsExportRequestDto } from "app/pages/sales-intelligence/sub-modules/contacts-search/helpers/dto";
import { makeFetchTopNContactsThunk } from "app/pages/sales-intelligence/sub-modules/contacts-search/store/effects";
import { createContactsTopNSearchResultsDto } from "app/pages/sales-intelligence/sub-modules/contacts-search/store/selectCombiners";
import { selectContactSearchResultsDto } from "app/pages/sales-intelligence/sub-modules/contacts-search/store/selectors";
import type {
    ContactIdCompanyPair,
    ContactsSearchResponseDto,
} from "app/pages/sales-intelligence/sub-modules/contacts-search/types/dto";
import type { FetchDomainsParams } from "app/pages/sales-intelligence/sub-modules/management/components/types";
import { createExportTopNDomainsToDynamicsThunk } from "app/pages/sales-intelligence/sub-modules/management/store/effects/dynamics/domainExport";
import { createExportTopNDomainsToHsThunk } from "app/pages/sales-intelligence/sub-modules/management/store/effects/hubspot/export";
import { createExportTopNDomainsToSfThunk } from "app/pages/sales-intelligence/sub-modules/management/store/effects/salesforce/export";
import * as actionCreators from "app/pages/sales-intelligence/sub-modules/opportunities/store/action-creators";
import { removeRecommendedDomains } from "app/pages/sales-intelligence/sub-modules/opportunities/store/action-creators";
import {
    excludeDuplicateOpportunitiesThunk,
    exportContactsToExcelThunk,
    updateListOpportunitiesThunk,
} from "app/pages/sales-intelligence/sub-modules/opportunities/store/effects";
import { selectHasExcludeDuplicatesLists } from "app/pages/sales-intelligence/sub-modules/opportunities/store/selectors";
import type { OpportunityListType } from "app/pages/sales-intelligence/sub-modules/opportunities/types";
import type { ThunkDependencies } from "single-spa/store/thunk-dependencies";
import type { ThunkDispatchCommon, ThunkGetState } from "single-spa/store/types";

import { fetchTopNLeadsDomains } from "../query/queryFn/fetchTopNLeadsDomains";

const fetchTopNLeadsDomainsAdapter = async (params: MoveTopNLeadsParams) => {
    return fetchTopNLeadsDomains({
        ...params,
        listParams: { id: params.currentList.opportunityListId, variant: ListVariant.Static },
    });
};

export const moveTopNLeadsToListThunk = createMoveTopNLeadsToListThunk(fetchTopNLeadsDomainsAdapter);

export const exportTopNLeadsToSfThunk = createExportTopNDomainsToSfThunk<
    FetchTopNLeadsDomainsFnParams & FetchDomainsParams
>(fetchTopNLeadsDomains);

export const exportTopNLeadsToHsThunk = createExportTopNDomainsToHsThunk<
    FetchTopNLeadsDomainsFnParams & FetchDomainsParams
>(fetchTopNLeadsDomains);

export const exportTopNLeadsToDynamicsThunk = createExportTopNDomainsToDynamicsThunk<
    FetchTopNLeadsDomainsFnParams & FetchDomainsParams
>(fetchTopNLeadsDomains);

const createFetchTopNContactsThunk = (topN: number, listParams: ListParams) => {
    return async (
        _dispatch: ThunkDispatchCommon,
        getState: ThunkGetState,
        deps: ThunkDependencies,
    ): Promise<ContactsSearchResponseDto> => {
        const state = getState();

        const dto = selectContactSearchResultsDto(state);
        const activeLeadsType = selectActiveLeadsType(state);
        const isAggregatedDataUsed = selectIsAggregatedDataUsed(state);

        const topNDto = createContactsTopNSearchResultsDto(dto, topN, isAggregatedDataUsed);

        const response = await deps.si.queryClient.fetchQuery({
            queryKey: opportunityContactsQueryKeys.tableData(listParams.id, activeLeadsType, topNDto),
            queryFn() {
                return deps.si.api.opportunityListPage.fetchContacts({
                    listParams,
                    dto: topNDto,
                    leadsType: activeLeadsType,
                });
            },
        });

        return response;
    };
};

export const fetchTopNContactsThunk = makeFetchTopNContactsThunk(createFetchTopNContactsThunk);

export function exportSelectedContactsToExcelThunk(
    listParams: ListParams,
    topNOrPairs: number | readonly ContactIdCompanyPair[],
    isSilent?: boolean,
) {
    return async function exportSelectedContactsToExcel(dispatch: ThunkDispatchCommon, getState: ThunkGetState) {
        const state = getState();

        const activeLeadsType = selectActiveLeadsType(state);
        const searchRequestDto = selectContactSearchResultsDto(state);

        const dto = createContactsExportRequestDto({
            searchRequestDto,
            topNOrPairs,
        });

        return dispatch(exportContactsToExcelThunk(listParams, dto, activeLeadsType, isSilent));
    };
}

export function addDomainsFromRecommendationsAndReFetchThunk(
    listId: OpportunityListType["opportunityListId"],
    domains: readonly string[],
) {
    return async function addDomainsFromRecommendationsAndReFetch(dispatch: ThunkDispatchCommon) {
        dispatch(removeRecommendedDomains(domains));

        try {
            await dispatch(updateListOpportunitiesThunk(listId, { opportunities: domains }));
        } catch (e) {
            Sentry.error(e.message, e);
        }
    };
}

export const shouldShowExcludeDuplicateOpportunitiesModalThunk =
    (listId: OpportunityListType["opportunityListId"], domainsToCheck: readonly string[]) =>
    async (dispatch: ThunkDispatchCommon, getState: ThunkGetState) => {
        const resultAction = await dispatch(excludeDuplicateOpportunitiesThunk(listId, domainsToCheck));

        const isSuccessAction = isActionOf(actionCreators.fetchExcludeListsDuplicatesAsync.success, resultAction);
        const shouldToggleModal = isSuccessAction && selectHasExcludeDuplicatesLists(getState());

        return shouldToggleModal;
    };

export function invalidateOpportunityListTablesQueriesThunk() {
    return async function invalidateOpportunityListTablesQueries(
        _dispatch: ThunkDispatchCommon,
        _getState: ThunkGetState,
        deps: ThunkDependencies,
    ) {
        return Promise.all(
            getOpportunityListTableQueryKeys().map((queryKey) => deps.si.queryClient.invalidateQueries({ queryKey })),
        );
    };
}
